import {
  ArrowLeft,
  BarChart3,
  ChevronDown,
  ChevronUp,
  Copy,
  Eye,
  HelpCircle,
  Loader2,
  Mail,
  Plus,
  Send,
  Settings,
  Users,
} from "lucide-react";
import { Campaign, CampaignsListProps } from "./types";
import { DEMO_IDS, generateDemoCampaign } from "@/mock/demo";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { formatCurrency, formatDate, formatNumber } from "@/utils/format";
import { Fragment, useEffect, useState } from "react";
import { usePaginatedQuery, useQuery } from "convex/react";

import AppDashboard from "../AppDashboard";
import { Button } from "@/components/ui/button";
import CreateCampaignDialog from "./CreateCampaignDialog";
import EditCampaignDialog from "./EditCampaignDialog";
import { Id } from "@cvx/_generated/dataModel";
import { InviteCreatorDialog } from "../InviteCreatorDialog";
import PlatformIcon from "../../Generic/PlatformIcon";
import StatusBadge from "../../Generic/StatusBadge";
import { TEAM_USER_IDS } from "@/constants/userIds";
import { api } from "@cvx/_generated/api";
import { useAccount } from "@/state/useAccount";
import { useNavigate } from "@tanstack/react-router";
import { useRef } from "react";
import { useToast } from "@/components/ui/use-toast";

export default function CampaignsList({
  appId,
  selectedCampaignId,
}: CampaignsListProps) {
  const navigate = useNavigate();
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState<Campaign | null>(null);
  const [duplicatingCampaign, setDuplicatingCampaign] =
    useState<Campaign | null>(null);
  const { toast } = useToast();
  const { demoModeEnabled, demoAppName } = useAccount();
  const [showInviteCreatorDialog, setShowInviteCreatorDialog] = useState(false);
  const [expandedCampaigns, setExpandedCampaigns] = useState<Set<string>>(
    new Set(),
  );
  const hasInitializedRef = useRef(false);

  // Get current user to check if they're a CreatorGrid team member
  const user = useQuery(api.app.getCurrentUser, {});
  const isCreatorGridTeam = user ? TEAM_USER_IDS.includes(user._id) : false;

  // Check if we're in demo mode (founder demo only)
  const isDemo =
    demoModeEnabled &&
    (appId as unknown as string) === (DEMO_IDS.appId as unknown as string);

  const {
    results: campaigns,
    status: campaignsStatus,
    loadMore: loadMoreCampaigns,
    isLoading: isLoadingCampaigns,
  } = usePaginatedQuery(
    api.campaigns.getCampaigns,
    !isDemo ? { appId } : "skip",
    { initialNumItems: 10 },
  );

  // Get aggregate stats across all campaigns
  const aggregateStats = useQuery(
    api.dashboard.getAppCampaignsAggregateStats,
    !isDemo ? { appId } : "skip",
  );

  // Use demo data when in demo mode
  const effectiveCampaigns = isDemo
    ? [generateDemoCampaign(demoAppName)]
    : campaigns;
  const effectiveIsLoading = isDemo ? false : isLoadingCampaigns;
  const effectiveStatus = isDemo ? "Exhausted" : campaignsStatus;

  // Check if any campaign has a prizePool to show Budget column
  const hasAnyCampaignWithPrizePool =
    effectiveCampaigns?.some(
      (campaign) =>
        campaign.prizePool !== undefined && campaign.prizePool !== null,
    ) ?? false;

  // Expand all campaigns with top performers by default (only once on initial load)
  useEffect(() => {
    if (
      !hasInitializedRef.current &&
      aggregateStats?.campaignStats &&
      effectiveCampaigns &&
      effectiveCampaigns.length > 0
    ) {
      const campaignsWithTopPerformers = new Set<string>();
      for (const campaign of effectiveCampaigns) {
        const campaignStat = aggregateStats.campaignStats.find(
          (stat) => stat.campaignId === campaign._id,
        );
        if (
          campaignStat?.topPerformers &&
          campaignStat.topPerformers.length > 0
        ) {
          campaignsWithTopPerformers.add(campaign._id);
        }
      }
      if (campaignsWithTopPerformers.size > 0) {
        setExpandedCampaigns(campaignsWithTopPerformers);
      }
      hasInitializedRef.current = true;
    }
  }, [aggregateStats?.campaignStats, effectiveCampaigns]);

  const handleCreateCampaign = () => {
    if (isDemo) {
      toast({
        title: "Demo Mode",
        description: "Campaign creation is not available in demo mode",
        variant: "destructive",
      });
      return;
    }
    setShowCreateDialog(true);
  };

  const handleCampaignClick = (campaignId: Id<"campaigns">) => {
    navigate({
      to: "/dashboard",
      search: { campaignId },
    });
  };

  const handleBackToCampaigns = () => {
    navigate({
      to: "/dashboard",
      search: {},
    });
  };

  const handleEditCampaign = (e: React.MouseEvent, campaign: Campaign) => {
    e.stopPropagation(); // Prevent the row click from firing
    if (isDemo) {
      toast({
        title: "Demo Mode",
        description: "Campaign editing is not available in demo mode",
        variant: "destructive",
      });
      return;
    }
    setEditingCampaign(campaign);
    setShowEditDialog(true);
  };

  const handleDuplicateCampaign = (e: React.MouseEvent, campaign: Campaign) => {
    e.stopPropagation(); // Prevent the row click from firing
    if (isDemo) {
      toast({
        title: "Demo Mode",
        description: "Campaign duplication is not available in demo mode",
        variant: "destructive",
      });
      return;
    }
    setDuplicatingCampaign(campaign);
    setShowCreateDialog(true);
  };

  const handleShareApplication = async () => {
    const url = `${window.location.origin}/campaigns?campaignId=${selectedCampaignId}`;
    await navigator.clipboard.writeText(url);
    toast({
      title: "Application Link Copied",
      description: "The application link has been copied to your clipboard",
    });
  };

  const toggleTopPerformers = (
    e: React.MouseEvent,
    campaignId: Id<"campaigns">,
  ) => {
    e.stopPropagation(); // Prevent the row click from firing
    setExpandedCampaigns((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(campaignId)) {
        newSet.delete(campaignId);
      } else {
        newSet.add(campaignId);
      }
      return newSet;
    });
  };

  // Show AppDashboard if a campaign is selected
  if (selectedCampaignId) {
    const selectedCampaign = effectiveCampaigns?.find(
      (c) => c._id === selectedCampaignId,
    );

    return (
      <div className="flex h-full w-full bg-secondary px-6  py-8 dark:bg-black">
        <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col gap-8">
          <div className="flex items-center justify-between px-5">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={handleBackToCampaigns}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="w-4 h-4" />
                Back to Campaigns
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-primary">
                  {selectedCampaign?.name || "Campaign"}
                </h1>
              </div>
            </div>

            <InviteCreatorDialog
              open={showInviteCreatorDialog}
              onOpenChange={setShowInviteCreatorDialog}
              campaignId={selectedCampaignId as Id<"campaigns">}
              maxEndDate={selectedCampaign?.endDate}
            />
            <div className="flex items-center gap-4">
              <Button
                variant="secondary"
                onClick={() => setShowInviteCreatorDialog(true)}
              >
                <Mail className="w-4 h-4 mr-2" />
                Invite Creator
              </Button>
              <Button variant="outline" onClick={handleShareApplication}>
                <Send className="w-4 h-4 mr-2" />
                Share Application Link
              </Button>
            </div>
          </div>
          <div className="flex-1">
            <AppDashboard campaignId={selectedCampaignId as Id<"campaigns">} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full bg-secondary px-6 py-8 dark:bg-black">
      <div className="z-10 mx-auto flex h-full w-full max-w-screen-xl flex-col gap-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary">Campaigns</h1>
            <p className="text-sm text-primary/60">
              Manage your marketing campaigns and creator partnerships
            </p>
          </div>
          <div className="flex items-center gap-2">
            {isCreatorGridTeam && (
              <Button
                variant="outline"
                onClick={() => navigate({ to: "/dashboard/meta" })}
              >
                <BarChart3 className="w-4 h-4 mr-2" />
                Meta Dashboard
              </Button>
            )}
            <Button onClick={handleCreateCampaign}>
              <Plus className="w-4 h-4 mr-2" />
              Create Campaign
            </Button>
          </div>
        </div>

        {/* Stats Cards Section - Only visible for CreatorGrid team members */}
        {isCreatorGridTeam && (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
            {/* Total Views Card */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-primary/60">
                  Total Views
                </h3>
                <Eye className="h-5 w-5 text-primary/60" />
              </div>
              <div className="space-y-3">
                <p className="text-3xl font-semibold text-primary">
                  {formatNumber(aggregateStats?.totalViews ?? 0)}
                </p>
                <p className="text-xs text-primary/60">Across all campaigns</p>
              </div>
            </div>

            {/* Total Videos by Platform Card */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-medium text-primary/60">
                  Total Videos Posted
                </h3>
                <Eye className="h-5 w-5 text-primary/60" />
              </div>
              <div className="space-y-3">
                <p className="text-3xl font-semibold text-primary">
                  {Object.values(aggregateStats?.videosByPlatform ?? {}).reduce(
                    (sum, count) => sum + count,
                    0,
                  )}
                </p>
                <div className="flex flex-wrap gap-2 mt-2">
                  {aggregateStats?.videosByPlatform &&
                    Object.entries(aggregateStats.videosByPlatform).map(
                      ([platform, count]) => (
                        <div
                          key={platform}
                          className="flex items-center gap-1.5 px-2 py-1 bg-secondary rounded-md"
                        >
                          <PlatformIcon platform={platform} />
                          <span className="text-xs text-primary/60">
                            {formatNumber(count)}
                          </span>
                        </div>
                      ),
                    )}
                </div>
              </div>
            </div>

            {/* Unique Creators by Campaign Type Card */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-medium text-primary/60">
                    Total Unique Creators
                  </h3>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <HelpCircle className="h-4 w-4 text-primary/40 hover:text-primary/60 cursor-help" />
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs" side="bottom">
                        <p className="mb-2 font-medium">
                          Total Unique Creators (Deduplicated)
                        </p>
                        <p className="text-sm">
                          The total counts each creator only once, even if they
                          participate in multiple campaign types. The breakdown
                          below shows how many unique creators are in each type,
                          but the same creator may appear in multiple
                          categories.
                        </p>
                        <p className="text-sm mt-2 text-primary/60">
                          Example: If 1 creator is in both CiR and Challenge
                          campaigns, the total is 1, but the breakdown shows 1
                          in each category.
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <Users className="h-5 w-5 text-primary/60" />
              </div>
              <div className="space-y-3">
                <p className="text-3xl font-semibold text-primary">
                  {aggregateStats?.totalUniqueCreators ?? 0}
                </p>
                <div className="flex flex-col gap-1.5 mt-2">
                  {aggregateStats?.uniqueCreatorsByType && (
                    <>
                      {aggregateStats.uniqueCreatorsByType.CiR > 0 && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-primary/60">
                            Creator in Residence:
                          </span>
                          <span className="font-medium text-primary">
                            {aggregateStats.uniqueCreatorsByType.CiR}
                          </span>
                        </div>
                      )}
                      {aggregateStats.uniqueCreatorsByType.influencer > 0 && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-primary/60">Influencer:</span>
                          <span className="font-medium text-primary">
                            {aggregateStats.uniqueCreatorsByType.influencer}
                          </span>
                        </div>
                      )}
                      {aggregateStats.uniqueCreatorsByType.challenge > 0 && (
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-primary/60">Challenge:</span>
                          <span className="font-medium text-primary">
                            {aggregateStats.uniqueCreatorsByType.challenge}
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Top Performing Content Card */}
            <div className="rounded-lg border border-border bg-card p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-primary/60">
                  Top Performing Content
                </h3>
                <Eye className="h-5 w-5 text-primary/60" />
              </div>
              <div className="mt-2 mb-4">
                {aggregateStats?.topContent &&
                  aggregateStats.topContent.length > 0 ? (
                  <div className="space-y-2">
                    {aggregateStats.topContent.map((content) => (
                      <div
                        key={content.videoId}
                        className="flex items-center justify-between text-sm"
                      >
                        <button
                          onClick={() => {
                            navigate({
                              to: "/dashboard",
                              search: { campaignId: content.campaignId },
                            });
                          }}
                          className="text-primary hover:underline truncate flex-1 mr-2 text-left cursor-pointer"
                          title={content.videoTitle}
                        >
                          {content.videoTitle}
                        </button>
                        <span className="font-semibold text-primary">
                          {formatNumber(content.views)}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-3xl font-semibold text-primary">
                    No videos
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <CreateCampaignDialog
          open={showCreateDialog}
          onOpenChange={(open) => {
            setShowCreateDialog(open);
            if (!open) {
              // Clear the duplicating campaign when dialog closes
              setDuplicatingCampaign(null);
            }
          }}
          appId={appId}
          campaignToDuplicate={duplicatingCampaign}
        />

        {editingCampaign && (
          <EditCampaignDialog
            open={showEditDialog}
            onOpenChange={(open) => {
              setShowEditDialog(open);
              if (!open) {
                // Clear the editing campaign when dialog closes to ensure fresh state
                setEditingCampaign(null);
              }
            }}
            campaign={editingCampaign}
          />
        )}

        <div className="rounded-lg border border-border bg-card">
          <div className="overflow-x-auto">
            {effectiveIsLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="animate-spin mx-auto" />
              </div>
            ) : !effectiveCampaigns || effectiveCampaigns.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="mb-4 text-lg text-primary/60">
                  No campaigns found
                </p>
                <p className="text-sm text-primary/60 mb-6">
                  Create your first campaign to start working with creators
                </p>
                <Button onClick={handleCreateCampaign}>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Campaign
                </Button>
              </div>
            ) : (
              <table className="w-full">
                <thead className="border-b border-border bg-secondary">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                      Campaign Name
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                      Type
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                      Visibility
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                      {isCreatorGridTeam ? "Creator Stats" : "Active Creators"}
                    </th>
                    {isCreatorGridTeam && (
                      <>
                        <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                          Views
                        </th>
                        <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                          Videos
                        </th>
                      </>
                    )}
                    {hasAnyCampaignWithPrizePool && (
                      <th className="px-6 py-3 text-right text-sm font-medium text-primary/60">
                        Budget Remaining
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                      End Date
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                      Created
                    </th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-primary/60">
                      Settings
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {effectiveCampaigns.map((campaign) => {
                    // Find matching campaign stats
                    const campaignStat = aggregateStats?.campaignStats?.find(
                      (stat) => stat.campaignId === campaign._id,
                    );
                    const topPerformers = campaignStat?.topPerformers ?? [];
                    const isExpanded = expandedCampaigns.has(campaign._id);

                    return (
                      <Fragment key={campaign._id}>
                        <tr
                          className="cursor-pointer hover:bg-secondary/50"
                          onClick={() => handleCampaignClick(campaign._id)}
                        >
                          <td className="px-6 py-4 text-sm text-primary font-medium">
                            <div className="flex items-center gap-2">
                              {topPerformers.length > 0 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="h-6 w-6 p-0 hover:bg-secondary"
                                  onClick={(e) =>
                                    toggleTopPerformers(e, campaign._id)
                                  }
                                >
                                  {isExpanded ? (
                                    <ChevronUp className="h-4 w-4" />
                                  ) : (
                                    <ChevronDown className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                              <span className="hover:underline">
                                {campaign.name}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-primary">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${campaign.type === "CiR"
                                ? "bg-blue-500/10 text-blue-500"
                                : campaign.type === "influencer"
                                  ? "bg-purple-500/10 text-purple-500"
                                  : "bg-green-500/10 text-green-500"
                                }`}
                            >
                              {campaign.type === "CiR"
                                ? "Creator in Residence"
                                : campaign.type === "influencer"
                                  ? "Influencer"
                                  : "Challenge"}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm">
                            <StatusBadge status={campaign.status} />
                          </td>
                          <td className="px-6 py-4 text-sm text-primary">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${campaign.isPublic
                                ? "bg-green-500/10 text-green-500"
                                : "bg-gray-500/10 text-gray-500"
                                }`}
                            >
                              {(() => {
                                if (!campaign.isPublic) {
                                  return "Private";
                                }

                                if (campaign.applicationNotRequired) {
                                  return "Public (Open to All)";
                                }

                                return "Public - Application Only";
                              })()}
                            </span>
                          </td>
                          <td className="px-6 py-4 text-sm text-primary">
                            {isCreatorGridTeam ? (
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-primary/60">
                                    Enrolled:
                                  </span>
                                  <span className="font-medium">
                                    {campaign.enrolledCreatorsCount || 0}
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-primary/60">
                                    Submitted:
                                  </span>
                                  {campaign.manualVideoReviewRequired ? (
                                    <span className="font-medium">
                                      {campaign.submittedCreatorsCount || 0}
                                    </span>
                                  ) : (
                                    <span className="font-medium text-primary/40">
                                      N/A
                                    </span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-primary/60">
                                    Approved:
                                  </span>
                                  <span className="font-medium">
                                    {campaign.approvedCreatorsCount || 0}
                                  </span>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center">
                                {campaign.approvedCreatorsCount || 0}
                              </div>
                            )}
                          </td>
                          {isCreatorGridTeam && (
                            <>
                              <td className="px-6 py-4 text-right text-sm text-primary">
                                {formatNumber(campaignStat?.totalViews ?? 0)}
                              </td>
                              <td className="px-6 py-4 text-right text-sm text-primary">
                                <div className="flex items-center justify-end gap-2">
                                  {campaignStat?.videosByPlatform &&
                                    Object.keys(campaignStat.videosByPlatform)
                                      .length > 0 && (
                                      <div className="flex items-center gap-1">
                                        {Object.entries(
                                          campaignStat.videosByPlatform,
                                        ).map(([platform, count]) => (
                                          <div
                                            key={platform}
                                            className="flex items-center gap-0.5"
                                            title={`${platform}: ${count}`}
                                          >
                                            <PlatformIcon platform={platform} />
                                            <span className="text-xs text-primary/60">
                                              {count}
                                            </span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                </div>
                              </td>
                            </>
                          )}
                          {hasAnyCampaignWithPrizePool && (
                            <td className="px-6 py-4 text-right text-sm text-primary">
                              {campaign.prizePool !== undefined &&
                                campaign.prizePool !== null ? (
                                formatCurrency(campaign.prizePool)
                              ) : (
                                <span className="text-primary/60">-</span>
                              )}
                            </td>
                          )}
                          <td className="px-6 py-4 text-sm text-primary">
                            {campaign.endDate ? (
                              <div className="flex flex-col">
                                <span>
                                  {formatDate(
                                    new Date(campaign.endDate).toISOString(),
                                  )}
                                </span>
                                {(() => {
                                  const endDate = new Date(campaign.endDate);
                                  const now = new Date();
                                  const diffTime =
                                    endDate.getTime() - now.getTime();
                                  const diffDays = Math.ceil(
                                    diffTime / (1000 * 60 * 60 * 24),
                                  );
                                  if (diffDays > 0) {
                                    return (
                                      <span className="text-xs text-primary/60">
                                        {diffDays} day
                                        {diffDays !== 1 ? "s" : ""} left
                                      </span>
                                    );
                                  } else if (diffDays === 0) {
                                    return (
                                      <span className="text-xs text-primary/60">
                                        Ends today
                                      </span>
                                    );
                                  }
                                  // Don't show anything for past dates
                                  return null;
                                })()}
                              </div>
                            ) : (
                              <span className="text-primary/60">
                                No end date
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-primary">
                            {formatDate(
                              new Date(campaign._creationTime).toISOString(),
                            )}
                          </td>
                          <td className="px-6 py-4 text-sm text-primary">
                            <div className="flex items-center gap-2">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) =>
                                  handleDuplicateCampaign(e, campaign)
                                }
                                className="hover:bg-secondary"
                                title="Duplicate campaign"
                              >
                                <Copy className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => handleEditCampaign(e, campaign)}
                                className="hover:bg-secondary"
                                title="Edit campaign"
                              >
                                <Settings className="w-4 h-4" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                        {/* Top Performers Row */}
                        {topPerformers.length > 0 && isExpanded && (
                          <tr className="bg-secondary/30 hover:bg-secondary/40">
                            <td
                              colSpan={
                                isCreatorGridTeam
                                  ? hasAnyCampaignWithPrizePool
                                    ? 11
                                    : 10
                                  : hasAnyCampaignWithPrizePool
                                    ? 9
                                    : 8
                              }
                              className="px-6 py-4"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-xs font-medium text-primary/60 uppercase tracking-wide">
                                  Top Performers:
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-4">
                                {topPerformers.map((performer, index) => (
                                  <div
                                    key={performer.creatorId}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate({
                                        to: "/creators/$creatorId",
                                        params: {
                                          creatorId: performer.creatorId,
                                        },
                                      });
                                    }}
                                    className="flex items-center gap-3 bg-card border border-border rounded-lg px-4 py-3 min-w-[200px] cursor-pointer hover:bg-secondary/50 transition-colors hover:border-primary/20"
                                  >
                                    <div className="flex-shrink-0">
                                      <span className="text-sm font-bold text-primary/40 mr-2">
                                        #{index + 1}
                                      </span>
                                      <img
                                        src={
                                          performer.avatarUrl ||
                                          "/default-avatar.png"
                                        }
                                        alt={performer.name}
                                        className="h-10 w-10 rounded-full object-cover"
                                      />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-sm text-primary truncate">
                                        {performer.name}
                                      </p>
                                      <div className="flex gap-3 mt-1 text-xs text-primary/60">
                                        <span>
                                          {formatNumber(performer.totalViews)}{" "}
                                          views
                                        </span>
                                        <span>
                                          {formatNumber(performer.totalLikes)}{" "}
                                          likes
                                        </span>
                                        <span>
                                          {performer.totalVideos} videos
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
          {effectiveCampaigns &&
            effectiveCampaigns.length > 0 &&
            effectiveStatus === "CanLoadMore" && (
              <div className="flex justify-center p-4 border-t border-border">
                <Button variant="outline" onClick={() => loadMoreCampaigns(10)}>
                  Load More
                </Button>
              </div>
            )}
        </div>
      </div>
    </div>
  );
}
